import * as Promise from 'bluebird';
import dayjs from 'dayjs';
import { getLogger } from '../logger';
import axios from 'axios';
import * as classroomApi from '../api/classroomApi';
import * as bookApi from '../api/bookApi';
import * as backofficeApi from '../api/backofficeApi';
import * as groupApi from '../api/groupApi';
import * as userApi from '../api/userApi';

const debug = getLogger('services:apiLoader');

export class ApiLoaderService {
  static _instance;
  loadActivated;

  constructor() {
    this.loadActivated = true;
  }

  static getInstance() {
    if (!ApiLoaderService._instance) {
      ApiLoaderService._instance = new ApiLoaderService();
    }
    return ApiLoaderService._instance;
  }

  callDataByTeacher(userId) {
    if (!this.loadActivated) return;
    return this.callMetadataBooksOfActiveClassroomsAndStudents(userId).then(() => {
      if (!this.loadActivated) return;
      return classroomApi.getArchiveClassrooms(userId).then(() => {
        return Promise.resolve(true);
      });
    });
  }

  callDataByStudent(userId) {
    if (!this.loadActivated) return;
    return this.callMetadataBooksOfActiveClassrooms(userId);
  }

  async callMetadataBooksOfActiveClassrooms(userId) {
    if (!userId) {
      debug.error('calling "callMetadataBooksOfActiveClassrooms" without userId: "%s"', userId);
      return;
    }

    if (!this.loadActivated) return;

    const resClassrooms = await classroomApi.getActiveClassrooms(userId);
    let now = dayjs()
      .add(1, 'days')
      .format('YYYY-MM-DD');
    const oneMonthBefore = dayjs()
      .subtract(1, 'months')
      .format('YYYY-MM-DD');

    await Promise.mapSeries(resClassrooms.data, async classroom => {
      if (!this.loadActivated) return;
      await this.callTeachersAvatars(classroom.id);
      await classroomApi.getClassroom(classroom.id);
      await backofficeApi.getStudentResults(oneMonthBefore, now, classroom.sectionId, userId);
    });
  }

  async callMetadataBooksOfActiveClassroomsAndStudents(userId) {
    if (!userId) {
      debug.error('[callMetadataBooksOfActiveClassrooms] missing userId');
      return;
    }

    if (!this.loadActivated) return;

    let now = dayjs()
      .add(1, 'days')
      .format('YYYY-MM-DD');
    const oneMonthBefore = dayjs()
      .subtract(1, 'months')
      .format('YYYY-MM-DD');
    const twoMonthsBefore = dayjs()
      .subtract(2, 'months')
      .format('YYYY-MM-DD');
    const threeMonthsBefore = dayjs()
      .subtract(3, 'months')
      .format('YYYY-MM-DD');

    const resClassrooms = await classroomApi.getActiveClassrooms(userId);
    await Promise.mapSeries(resClassrooms.data, async classroom => {
      if (!this.loadActivated) return;
      await classroomApi.getClassroom(classroom.id);
      await this.callGroupStudentsAndStudents(classroom.id);
      await this.callGetBooks(classroom);
      await backofficeApi.getStudentResults(oneMonthBefore, now, classroom.sectionId);
      await backofficeApi.getStudentResults(twoMonthsBefore, now, classroom.sectionId);
      await backofficeApi.getStudentResults(threeMonthsBefore, now, classroom.sectionId);
    });
  }

  async callGetBooks(classroom) {
    if (!this.loadActivated) return undefined;
    const booksResponse = await bookApi.getBooks(classroom.id);
    return Promise.mapSeries(booksResponse.data, async b => {
      try {
        if (!this.loadActivated) return;
        await bookApi.getBook(classroom.id, b.id);
      } catch (err) {
        debug.error('error getting content book %s', err);
      }
    });
  }

  callGroupStudentsAndStudents(classroomId) {
    if (classroomId) {
      if (!this.loadActivated) return undefined;

      return classroomApi.getStudents(classroomId).then(() => {
        if (!this.loadActivated) return;

        return classroomApi.getGroups(classroomId).then(groupsRes => {
          let groups = groupsRes.data;
          return Promise.mapSeries(groups, group => {
            if (!this.loadActivated) return;

            return groupApi.getStudents(group.id).then(() => {
              return Promise.resolve(true);
            });
          });
        });
      });
    }

    debug.error('calling callGroupStudentsAndStudents without classroomId: %s', classroomId);

    return Promise.reject(false);
  }

  async callTeachersAvatars(classroomId) {
    if (classroomId) {
      let images = [];
      let teachers = await classroomApi.getClassroomTeachers(classroomId);
      teachers.forEach(async teacher => {
        let user = await userApi.getUser(teacher._id);
        const axiosWithoutAuth = axios.create({
          baseURL: user.data.picture,
          headers: {}
        });
        images.push(axiosWithoutAuth.get());
      });
      return await Promise.all(images);
    }

    debug.error('calling callGroupStudentsAndStudents without classroomId: %s', classroomId);
  }

  setLoadActivated(value = true) {
    let msg = value ? 'activated' : 'desactivated';
    debug('the api loader is %s', msg);
    this.loadActivated = value;
  }
}
